import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { MuiMaterials } from "../@index.js";
import * as global from "../data/global";
import { createApiEndpoint } from "../index.js";
import { ModalChrome, ModalBody, ModalContentTile, SiteSelectPhase, SITE_WINDOW_IDS, ALL_SITES_OPTION, outlinedActionButtonSx, containedActionButtonSx, CenteredLoadingOverlay, withScrollHintMenuProps, ensureSiteInfosLoaded, openSiteWindow } from "./windowChrome/index.js";
import { getLastScanAlertReasons, lastScanAlertTooltip } from "./lastScanAlert.js";

const VERSION_LIST_PAGE = 20;

function SiteCardLastScanLine({ lastScan, lastScanDate, taskSlots, isHms, isCcr, isLight }) {
    const label = lastScan || "N/A";
    const reasons = getLastScanAlertReasons({ lastScan, lastScanDate, taskSlots, isHms, isCcr });
    const isBlinking = reasons.length > 0;
    const tooltip = lastScanAlertTooltip(reasons);
    return (
        <MuiMaterials.Tooltip
            title={tooltip}
            arrow
            placement="top"
            disableHoverListener={!isBlinking}
            disableFocusListener={!isBlinking}
            disableTouchListener={!isBlinking}
        >
        <MuiMaterials.Typography
            className={isBlinking ? "last-scan-alert" : ""}
            sx={{
                display: "block",
                margin: "2px 0 0",
                width: "100%",
                color: isBlinking
                    ? (isLight ? "#d32f2f" : "#ff6b6b")
                    : (isLight ? "#2d2d2d" : "#a8abb1"),
                fontSize: { xs: 10, sm: 11, md: 12 },
                fontWeight: 500,
                opacity: 0.9,
                letterSpacing: "0.3px",
                textTransform: "uppercase",
                fontFamily: "monospace",
                ...(isBlinking ? (isLight ? {
                    background: "none",
                    backgroundClip: "none",
                    WebkitBackgroundClip: "none",
                    WebkitTextFillColor: "inherit",
                    filter: "none",
                    fontWeight: 700,
                    textShadow: "0 0 8px rgba(211, 47, 47, 0.6)",
                } : {
                    background: "linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    filter: "drop-shadow(0 1px 2px rgba(255, 107, 107, 0.3))",
                }) : {
                    background: "none",
                    backgroundClip: "none",
                    WebkitBackgroundClip: "none",
                    WebkitTextFillColor: "inherit",
                    filter: "none",
                }),
            }}
        >
            {isBlinking ? "⚠ " : "📅 "}{label}
        </MuiMaterials.Typography>
        </MuiMaterials.Tooltip>
    );
}

/**
 * Keeps comment text in local state so typing does not re-render the whole
 * Site Versioning modal (site pins + version dropdowns).
 */
const ReleaseCommentEditor = React.memo(function ReleaseCommentEditor({
    version,
    datePublished,
    savedComments,
    formatVersionDate,
    text,
    border,
    sectionBg,
    fieldBg,
    getThemeColor,
    disabled,
    onSaved,
    onMessage,
    onRequestDelete,
}) {
    const [draft, setDraft] = useState(() => String(savedComments ?? ""));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraft(String(savedComments ?? ""));
    }, [version, savedComments]);

    const commentDirty = draft !== String(savedComments ?? "");

    const handleSave = async () => {
        const ver = String(version || "").trim();
        if (!ver || !commentDirty) return;
        setSaving(true);
        onMessage?.(null);
        try {
            const ok = await createApiEndpoint().updateTemputechReleaseComments("direct", ver, draft);
            if (!ok) {
                onMessage?.({ type: "error", text: `Failed to save comments for ${ver}.` });
                return;
            }
            onSaved?.(ver, draft);
            onMessage?.({ type: "success", text: `Saved comments for ${ver}.` });
        } catch {
            onMessage?.({ type: "error", text: `Failed to save comments for ${ver}.` });
        } finally {
            setSaving(false);
        }
    };

    return (
        <MuiMaterials.Box
            sx={{
                backgroundColor: commentDirty ? getThemeColor("#fff8e6", "#3d361f") : fieldBg,
                border: `1px solid ${border}`,
                borderRadius: "8px",
                p: 1.5,
                mb: 1,
            }}
        >
            <MuiMaterials.Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                }}
            >
                <MuiMaterials.Typography
                    sx={{
                        fontWeight: 600,
                        color: text,
                        fontSize: "14px",
                        flex: "1 1 auto",
                    }}
                >
                    {version}
                    {formatVersionDate(datePublished) ? ` · ${formatVersionDate(datePublished)}` : ""}
                </MuiMaterials.Typography>
                <MuiMaterials.Button
                    variant="contained"
                    size="small"
                    disabled={!commentDirty || saving || disabled}
                    onClick={handleSave}
                    sx={containedActionButtonSx()}
                >
                    {saving ? "Saving…" : "Save comments"}
                </MuiMaterials.Button>
                <MuiMaterials.Button
                    variant="outlined"
                    size="small"
                    disabled={saving || disabled}
                    onClick={() => onRequestDelete?.(version)}
                    sx={outlinedActionButtonSx()}
                >
                    Delete
                </MuiMaterials.Button>
            </MuiMaterials.Box>
            <MuiMaterials.TextField
                size="small"
                label="Comments"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={saving || disabled}
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                sx={{ backgroundColor: fieldBg }}
            />
        </MuiMaterials.Box>
    );
});

function VersionListLimitBar({ total, limit, subtle, disabled, onMore, onAll, onLatest, sx }) {
    if (!total || total <= VERSION_LIST_PAGE) return null;
    const showingAll = limit == null || limit >= total;
    const showing = showingAll ? total : Math.min(limit, total);
    return (
        <MuiMaterials.Box
            sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 1, ...sx }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px" }}>
                Showing {showing} of {total}
            </MuiMaterials.Typography>
            {!showingAll ? (
                <>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        disabled={disabled}
                        onClick={onMore}
                        sx={outlinedActionButtonSx()}
                    >
                        Show 20 more
                    </MuiMaterials.Button>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        disabled={disabled}
                        onClick={onAll}
                        sx={outlinedActionButtonSx()}
                    >
                        Show all
                    </MuiMaterials.Button>
                </>
            ) : (
                <MuiMaterials.Button
                    size="small"
                    variant="outlined"
                    disabled={disabled}
                    onClick={onLatest}
                    sx={outlinedActionButtonSx()}
                >
                    Show latest 20
                </MuiMaterials.Button>
            )}
        </MuiMaterials.Box>
    );
}

const SiteVersioningModal = ({ isModalOpen, setIsModalOpen, filterToSiteGuid }) => {
    const [currentTheme, setCurrentTheme] = useState(global.gw.theme || "dark");
    const [forceUpdate, setForceUpdate] = useState(0);
    const [loading, setLoading] = useState(false);
    const [savingGuid, setSavingGuid] = useState(null);
    const [sites, setSites] = useState([]);
    const [versions, setVersions] = useState([]);
    const [draftVersions, setDraftVersions] = useState({});
    const [message, setMessage] = useState(null);
    const [pickerSiteGuid, setPickerSiteGuid] = useState("");
    const [selectedSiteGuid, setSelectedSiteGuid] = useState("");
    const [deleteConfirmVersion, setDeleteConfirmVersion] = useState(null);
    const [deletingVersion, setDeletingVersion] = useState(null);
    const [purgeConfirmOpen, setPurgeConfirmOpen] = useState(false);
    const [purgingUnused, setPurgingUnused] = useState(false);
    const [versionListLimit, setVersionListLimit] = useState(VERSION_LIST_PAGE);
    const [otaVersionsOpen, setOtaVersionsOpen] = useState(false);
    /** Normalized siteGuid -> latest open issue text, shown the same way the site cards show it. */
    const [siteIssues, setSiteIssues] = useState(() => {
        const cached = global.gw.customerSitesIssuesCache || {};
        const seeded = {};
        Object.keys(cached).forEach((guid) => {
            if (guid && cached[guid]) seeded[global.normalizeGuidForCompare(guid)] = cached[guid];
        });
        return seeded;
    });
    const [lastScanByGuid, setLastScanByGuid] = useState({});
    const scrollContainerRef = useRef(null);

    const forwardWheelToScroller = useCallback((e) => {
        const scroller = scrollContainerRef.current;
        if (!scroller) return;
        // Open dropdown menus are portaled; leave their scroll alone.
        if (e.target instanceof Element) {
            const portaledMenu = e.target.closest(
                "[role='listbox'], .MuiMenu-list, .MuiPopover-root",
            );
            if (portaledMenu && !scroller.contains(portaledMenu)) return;
        }
        scroller.scrollTop += e.deltaY;
    }, []);


    const getThemeColor = useCallback(
        (lightColor, darkColor) => (currentTheme === "light" ? lightColor : darkColor),
        [currentTheme],
    );

    const text = getThemeColor("#333333", "#f4cc8e");
    const subtle = getThemeColor("#666666", "#a8abb1");
    const bg = getThemeColor("#ffffff", "#2d2d2d");
    const border = getThemeColor("#e0e0e0", "#3d3d3d");
    const sectionBg = getThemeColor("#f8f8f8", "#252525");
    const fieldBg = getThemeColor("#ffffff", "#2d2d2d");

    const modalTheme = useMemo(() => {
        const isLight = currentTheme === "light";
        const themeText = isLight ? "#333333" : "#f4cc8e";
        const themeBg = isLight ? "#ffffff" : "#2d2d2d";
        const themeBorder = isLight ? "#cccccc" : "rgba(244, 204, 142, 0.5)";
        const themeBorderHover = isLight ? "#666666" : "#f4cc8e";
        return MuiMaterials.createTheme({
            palette: {
                mode: isLight ? "light" : "dark",
                text: { primary: themeText, secondary: themeText },
            },
            components: {
                MuiInputLabel: {
                    styleOverrides: {
                        root: { color: isLight ? "#666666" : "#a8abb1" },
                        shrink: { color: themeText },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: themeText,
                            backgroundColor: themeBg,
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: themeBorder },
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: themeBorderHover },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: themeBorderHover },
                        },
                        input: {
                            color: themeText,
                            WebkitTextFillColor: themeText,
                        },
                    },
                },
                MuiSelect: {
                    styleOverrides: {
                        icon: { color: themeText },
                    },
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: {
                            color: themeText,
                            backgroundColor: themeBg,
                            "&:hover": {
                                backgroundColor: isLight ? "#f5f5f5" : "#3d3d3d",
                            },
                            "&.Mui-selected": {
                                backgroundColor: isLight ? "#eeeeee" : "#3d361f",
                                "&:hover": {
                                    backgroundColor: isLight ? "#e8e8e8" : "#4a4228",
                                },
                            },
                        },
                    },
                },
            },
        });
    }, [currentTheme]);

    useEffect(() => {
        const handleThemeChange = () => {
            setCurrentTheme(global.gw.theme || "dark");
            setForceUpdate((prev) => prev + 1);
        };
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            const currentThemeValue = global.gw.theme || "dark";
            if (currentThemeValue !== currentTheme) {
                setCurrentTheme(currentThemeValue);
                setForceUpdate((prev) => prev + 1);
            }
        }, 100);
        return () => clearInterval(interval);
    }, [currentTheme]);

    const loadCatalog = useCallback(async () => {
        if (!global.gw.auth || !global.gw.email) return;
        setLoading(true);
        setMessage(null);
        try {
            const res = await createApiEndpoint().fetchCustomerSiteVersions("direct");
            const catalog = res?.data?.fetchCustomerSiteVersions ?? res?.fetchCustomerSiteVersions ?? null;
            const siteList = Array.isArray(catalog?.sites) ? catalog.sites : [];
            const versionList = Array.isArray(catalog?.versions) ? catalog.versions : [];
            setSites(siteList);
            setVersions(versionList);
            const drafts = {};
            siteList.forEach((s) => {
                drafts[s.customerSiteGuid] = s.version || "";
            });
            setDraftVersions(drafts);
        } catch {
            setSites([]);
            setVersions([]);
            setDraftVersions({});
            setMessage({ type: "error", text: "Failed to load site versions." });
        } finally {
            setLoading(false);
        }
    }, []);

    const loadIssues = useCallback(async () => {
        if (!global.gw.auth || !global.gw.email) return;
        try {
            const rows = await createApiEndpoint().fetchAllSiteIssuesForAdmin("direct");
            const latest = new Map();
            (Array.isArray(rows) ? rows : []).forEach((row) => {
                if (!row?.siteGuid || !row?.issue || row.dateIssueResolved) return;
                const key = global.normalizeGuidForCompare(row.siteGuid);
                const created = row.dateCreated ? new Date(row.dateCreated).getTime() : 0;
                const existing = latest.get(key);
                if (!existing || created >= existing.created) latest.set(key, { issue: row.issue, created });
            });
            const map = {};
            latest.forEach((value, key) => {
                map[key] = value.issue;
            });
            setSiteIssues(map);
        } catch {
            // Keep whatever issues are already displayed; versioning still works without them.
        }
    }, []);

    const refreshLastScans = useCallback(() => {
        const map = {};
        (global.gw.siteInfos || []).forEach((s) => {
            if (!s?.siteGuid) return;
            map[global.normalizeGuidForCompare(s.siteGuid)] = {
                lastScan: s.lastScan || "N/A",
                lastScanDate: s.lastScanDate || null,
                taskSlots: s.taskSlots || [],
                isHms: !!s.isHms,
                isCcr: !!s.isCcr,
            };
        });
        setLastScanByGuid(map);
    }, []);

    useEffect(() => {
        if (!isModalOpen[0]) {
            setPickerSiteGuid("");
            setSelectedSiteGuid("");
            return;
        }
        setMessage(null);
        if (filterToSiteGuid) {
            setPickerSiteGuid(filterToSiteGuid);
            setSelectedSiteGuid(filterToSiteGuid);
        }
        loadCatalog();
        loadIssues();
        refreshLastScans();
        ensureSiteInfosLoaded().then(() => refreshLastScans());
    }, [isModalOpen, filterToSiteGuid, loadCatalog, loadIssues, refreshLastScans]);

    // Apply carried siteGuid before paint so tool-switching does not flash the picker.
    useLayoutEffect(() => {
        if (!isModalOpen[0] || !filterToSiteGuid) return;
        setPickerSiteGuid(filterToSiteGuid);
        setSelectedSiteGuid(filterToSiteGuid);
    }, [isModalOpen, filterToSiteGuid]);

    useEffect(() => {
        if (!isModalOpen[0]) return;
        const onIssuesRefreshed = () => loadIssues();
        window.addEventListener("siteIssuesRefreshed", onIssuesRefreshed);
        return () => window.removeEventListener("siteIssuesRefreshed", onIssuesRefreshed);
    }, [isModalOpen, loadIssues]);

    // Document capture: Select/combobox can still eat wheel before the scroller bubble path.
    useEffect(() => {
        if (!isModalOpen[0]) return undefined;

        const onWheel = (e) => {
            const scroller = scrollContainerRef.current;
            if (!scroller) return;
            const target = e.target;
            if (!(target instanceof Element) || !scroller.contains(target)) return;

            const portaledMenu = target.closest(
                "[role='listbox'], .MuiMenu-list, .MuiPopover-root",
            );
            if (portaledMenu && !scroller.contains(portaledMenu)) return;

            const textarea = target.closest("textarea");
            if (textarea instanceof HTMLTextAreaElement && scroller.contains(textarea)) {
                if (textarea.scrollHeight > textarea.clientHeight + 1) {
                    if (e.deltaY < 0 && textarea.scrollTop > 0) return;
                    if (
                        e.deltaY > 0 &&
                        textarea.scrollTop + textarea.clientHeight < textarea.scrollHeight - 1
                    ) {
                        return;
                    }
                }
            }

            const { deltaY } = e;
            if (!deltaY) return;
            const maxTop = scroller.scrollHeight - scroller.clientHeight;
            if (maxTop <= 0) return;

            e.preventDefault();
            e.stopPropagation();
            scroller.scrollTop = Math.max(0, Math.min(maxTop, scroller.scrollTop + deltaY));
        };

        document.addEventListener("wheel", onWheel, { passive: false, capture: true });
        return () => document.removeEventListener("wheel", onWheel, { capture: true });
    }, [isModalOpen, forceUpdate, currentTheme]);

    const versionOptions = useMemo(() => {
        const byVersion = new Map();
        (versions || []).forEach((entry) => {
            const version =
                typeof entry === "string"
                    ? entry.trim()
                    : String(entry?.version || "").trim();
            if (!version) return;
            const datePublished =
                typeof entry === "string" ? null : entry?.datePublished ?? null;
            const comments =
                typeof entry === "string" ? "" : String(entry?.comments ?? "");
            if (!byVersion.has(version.toLowerCase())) {
                byVersion.set(version.toLowerCase(), { version, datePublished, comments });
            }
        });
        Object.values(draftVersions).forEach((v) => {
            const version = String(v || "").trim();
            if (!version) return;
            const key = version.toLowerCase();
            if (!byVersion.has(key)) {
                byVersion.set(key, {
                    version,
                    datePublished: null,
                    comments: "",
                });
            }
        });

        const parse = (x) => {
            const cleaned = String(x).trim().replace(/^v/i, "");
            const parts = cleaned.split(".").map((n) => parseInt(n, 10) || 0);
            while (parts.length < 4) parts.push(0);
            return parts;
        };

        return Array.from(byVersion.values()).sort((a, b) => {
            const pa = parse(a.version);
            const pb = parse(b.version);
            for (let i = 0; i < 4; i++) {
                if (pa[i] !== pb[i]) return pb[i] - pa[i];
            }
            return String(b.version).localeCompare(String(a.version));
        });
    }, [versions, draftVersions]);

    const publishedVersionRows = useMemo(() => {
        return (versionOptions || []).filter((o) => {
            const match = (versions || []).find((entry) => {
                if (typeof entry === "string") return false;
                return (
                    String(entry?.version || "").toLowerCase() ===
                    String(o.version || "").toLowerCase()
                );
            });
            return !!match && match.isPublished === true;
        });
    }, [versionOptions, versions]);

    const visiblePublishedRows = useMemo(() => {
        if (!versionListLimit || versionListLimit >= publishedVersionRows.length) {
            return publishedVersionRows;
        }
        return publishedVersionRows.slice(0, versionListLimit);
    }, [publishedVersionRows, versionListLimit]);

    const showVersionListMore = useCallback(() => {
        setVersionListLimit((current) => (current || VERSION_LIST_PAGE) + VERSION_LIST_PAGE);
    }, []);

    const showVersionListAll = useCallback(() => {
        setVersionListLimit(null);
    }, []);

    const showVersionListLatest = useCallback(() => {
        setVersionListLimit(VERSION_LIST_PAGE);
    }, []);

    const formatVersionDate = useCallback((dateStr) => {
        if (!dateStr) return "";
        try {
            return new Date(dateStr).toLocaleString(
                "en-us",
                global.dateFormat || {
                    month: "2-digit",
                    day: "2-digit",
                    year: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                },
            );
        } catch {
            return "";
        }
    }, []);

    const normalizeVersion = useCallback((value) => {
        let s = String(value || "").trim();
        if (!s) return "";
        s = s.split("+")[0].trim();
        if (s.toLowerCase().startsWith("v")) s = s.slice(1);
        return s.toLowerCase();
    }, []);

    const versionsMatch = useCallback(
        (a, b) => {
            const left = normalizeVersion(a);
            const right = normalizeVersion(b);
            return Boolean(left && right && left === right);
        },
        [normalizeVersion],
    );

    const scopedSites = useMemo(() => {
        if (!selectedSiteGuid && !(isModalOpen[0] && filterToSiteGuid)) return [];
        const activeGuid = selectedSiteGuid || filterToSiteGuid;
        if (activeGuid === ALL_SITES_OPTION) {
            return [...sites].sort((a, b) =>
                String(a.siteName || "").localeCompare(String(b.siteName || ""), undefined, {
                    sensitivity: "base",
                }),
            );
        }
        return sites.filter(
            (s) =>
                global.normalizeGuidForCompare(s.customerSiteGuid) ===
                global.normalizeGuidForCompare(activeGuid),
        );
    }, [sites, selectedSiteGuid, isModalOpen, filterToSiteGuid]);

    const activeSiteGuid =
        selectedSiteGuid || (isModalOpen[0] ? filterToSiteGuid : "") || "";
    const phase = activeSiteGuid ? "edit" : "select";
    const isAllSites = activeSiteGuid === ALL_SITES_OPTION;

    const dirtySites = useMemo(
        () =>
            scopedSites.filter((site) => {
                const draft = (draftVersions[site.customerSiteGuid] || "").trim();
                return Boolean(draft) && draft !== (site.version || "");
            }),
        [scopedSites, draftVersions],
    );
    const unsavedAllSites = isAllSites && dirtySites.length > 0;

    const keepUnsavedAllSites = useCallback((nextGuid) => {
        if (!unsavedAllSites || !nextGuid || nextGuid === ALL_SITES_OPTION) return false;
        setMessage({
            type: "info",
            text: "Save version changes before opening a site.",
        });
        return true;
    }, [unsavedAllSites]);

    const handleClose = useCallback(() => setIsModalOpen([false]), [setIsModalOpen]);

    const focusSiteInVersioning = useCallback((customerSiteGuid) => {
        if (!customerSiteGuid || customerSiteGuid === ALL_SITES_OPTION) return;
        const want = global.normalizeGuidForCompare?.(customerSiteGuid)
            ?? String(customerSiteGuid).toLowerCase();
        const match = (global.gw.siteInfos || []).find((s) => {
            const have = global.normalizeGuidForCompare?.(s?.siteGuid)
                ?? String(s?.siteGuid || "").toLowerCase();
            return have === want;
        });
        const guid = match?.siteGuid || customerSiteGuid;
        setPickerSiteGuid(guid);
        setSelectedSiteGuid(guid);
        openSiteWindow(SITE_WINDOW_IDS.versioning, guid);
    }, []);

    const handleVersionChange = (siteGuid, nextVersion) => {
        setDraftVersions((prev) => ({ ...prev, [siteGuid]: nextVersion }));
        setMessage(null);
    };

    const handleCommentSaved = useCallback((version, nextComments) => {
        const ver = String(version || "").trim();
        setVersions((prev) =>
            (prev || []).map((entry) => {
                const v = typeof entry === "string" ? entry : entry?.version;
                if (String(v || "").toLowerCase() !== ver.toLowerCase()) return entry;
                if (typeof entry === "string") return { version: entry, comments: nextComments, isPublished: true };
                return { ...entry, comments: nextComments };
            }),
        );
    }, []);

    const handleRequestDeleteVersion = useCallback((version) => {
        const ver = String(version || "").trim();
        if (!ver) return;
        setMessage(null);
        setDeleteConfirmVersion(ver);
    }, []);

    const handleConfirmDeleteVersion = async () => {
        const ver = String(deleteConfirmVersion || "").trim();
        if (!ver) {
            setDeleteConfirmVersion(null);
            return;
        }
        setDeletingVersion(ver);
        setMessage(null);
        try {
            const result = await createApiEndpoint().deleteTemputechRelease("direct", ver);
            if (result?.sites?.length) {
                setMessage({
                    type: "error",
                    text: `Version ${ver} was not deleted. ${result.sites.join("; ")}.`,
                });
                setDeleteConfirmVersion(null);
                return;
            }
            if (!result?.ok) {
                setMessage({
                    type: "error",
                    text: result?.message || `Failed to delete version ${ver}.`,
                });
                return;
            }
            setVersions((prev) =>
                (prev || []).filter((entry) => {
                    const v = typeof entry === "string" ? entry : entry?.version;
                    return String(v || "").toLowerCase() !== ver.toLowerCase();
                }),
            );
            setMessage({
                type: "success",
                text: `Deleted OTA version ${ver} from the server (package files and database note).`,
            });
            setDeleteConfirmVersion(null);
        } catch {
            setMessage({ type: "error", text: `Failed to delete version ${ver}.` });
        } finally {
            setDeletingVersion(null);
        }
    };

    const handleConfirmPurgeUnused = async () => {
        setPurgingUnused(true);
        setMessage(null);
        try {
            const result = await createApiEndpoint().deleteUnusedTemputechReleases("direct");
            if (!result?.ok) {
                setMessage({
                    type: "error",
                    text: result?.message || "Failed to remove unused versions.",
                });
                return;
            }
            setPurgeConfirmOpen(false);
            await loadCatalog();
            setMessage({ type: "success", text: result.message });
        } catch {
            setMessage({ type: "error", text: "Failed to remove unused versions." });
        } finally {
            setPurgingUnused(false);
        }
    };

    const pinSiteVersion = async (site) => {
        const siteGuid = site.customerSiteGuid;
        const nextVersion = (draftVersions[siteGuid] || "").trim();
        if (!nextVersion || nextVersion === (site.version || "")) {
            return { ok: false, skipped: true, site, nextVersion };
        }
        const ok = await createApiEndpoint().updateCustomerSiteVersion("direct", siteGuid, nextVersion);
        if (!ok) return { ok: false, skipped: false, site, nextVersion };
        setSites((prev) =>
            prev.map((s) =>
                s.customerSiteGuid === siteGuid
                    ? { ...s, version: nextVersion, getLatestVersion: false, dateModified: new Date().toISOString() }
                    : s,
            ),
        );
        setVersions((prev) => {
            const exists = (prev || []).some((entry) => {
                const v = typeof entry === "string" ? entry : entry?.version;
                return String(v || "").toLowerCase() === nextVersion.toLowerCase();
            });
            return exists ? prev : [...prev, { version: nextVersion, datePublished: new Date().toISOString() }];
        });
        return { ok: true, skipped: false, site, nextVersion };
    };

    const handleSave = async (site) => {
        const siteGuid = site.customerSiteGuid;
        const nextVersion = (draftVersions[siteGuid] || "").trim();
        if (!nextVersion) {
            setMessage({ type: "error", text: "Select a version before saving." });
            return;
        }
        if (nextVersion === (site.version || "")) {
            setMessage({ type: "info", text: `${site.siteName || "Site"} is already on ${nextVersion}.` });
            return;
        }

        setSavingGuid(siteGuid);
        setMessage(null);
        try {
            const result = await pinSiteVersion(site);
            if (!result.ok) {
                setMessage({ type: "error", text: `Failed to update ${site.siteName || "site"}.` });
                return;
            }
            setMessage({ type: "success", text: `${site.siteName || "Site"} pinned to ${nextVersion}.` });
        } catch {
            setMessage({ type: "error", text: `Failed to update ${site.siteName || "site"}.` });
        } finally {
            setSavingGuid(null);
        }
    };

    const handleSaveAllChanged = async () => {
        if (dirtySites.length === 0) {
            setMessage({ type: "info", text: "No version changes to save." });
            return;
        }

        setSavingGuid("__all__");
        setMessage(null);
        const saved = [];
        const failed = [];
        try {
            for (const site of dirtySites) {
                try {
                    const result = await pinSiteVersion(site);
                    if (result.ok) saved.push(site.siteName || site.customerSiteGuid);
                    else if (!result.skipped) failed.push(site.siteName || site.customerSiteGuid);
                } catch {
                    failed.push(site.siteName || site.customerSiteGuid);
                }
            }
            if (failed.length === 0) {
                setMessage({
                    type: "success",
                    text:
                        saved.length === 1
                            ? `${saved[0]} pinned.`
                            : `Pinned ${saved.length} sites.`,
                });
            } else if (saved.length === 0) {
                setMessage({ type: "error", text: `Failed to update ${failed.join(", ")}.` });
            } else {
                setMessage({
                    type: "error",
                    text: `Pinned ${saved.length} site${saved.length === 1 ? "" : "s"}. Failed: ${failed.join(", ")}.`,
                });
            }
        } finally {
            setSavingGuid(null);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        try {
            return new Date(dateStr).toLocaleString(
                "en-us",
                global.dateFormat || {
                    month: "2-digit",
                    day: "2-digit",
                    year: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                },
            );
        } catch {
            return String(dateStr);
        }
    };

    /** reportedVersionUtc is UTC; GraphQL/SQL often omit Z, so force UTC parse then show local. */
    const formatReportedUtc = (dateStr) => {
        if (!dateStr) return "—";
        try {
            let s = String(dateStr).trim();
            if (!s) return "—";
            if (s.includes(" ") && !s.includes("T")) s = s.replace(" ", "T");
            if (!/[zZ]$/.test(s) && !/[+-]\d{2}:?\d{2}$/.test(s)) s = `${s}Z`;
            return new Date(s).toLocaleString(
                "en-us",
                global.dateFormat || {
                    month: "2-digit",
                    day: "2-digit",
                    year: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                },
            );
        } catch {
            return String(dateStr);
        }
    };

    return (
        <MuiMaterials.ThemeProvider theme={modalTheme}>
            <ModalChrome
                open={isModalOpen[0]}
                onClose={() => setIsModalOpen([false])}
                title="Site Versioning"
                toolTitle="Versioning"
                siteWindowId={SITE_WINDOW_IDS.versioning}
                siteGuid={activeSiteGuid || null}
                showHeaderSiteSelect={phase === "edit"}
                onHeaderSiteChange={(guid) => {
                    if (keepUnsavedAllSites(guid)) return;
                    setPickerSiteGuid(guid);
                    setSelectedSiteGuid(guid);
                }}
                headerFilterSite={(s) => !s.isHms}
                headerIncludeAllSites
                actions={
                    phase === "edit" ? (
                    <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, minWidth: 0 }}>
                        <MuiMaterials.Tooltip
                            arrow
                            placement="bottom"
                            title={
                                <span>
                                    {isAllSites
                                        ? "Select a version for each site, then click Save in the header to update every changed site. You can still Save one row at a time. Click a site to open Versioning for that site only. Running shows what TemputechService last reported; red text means it does not match the pin. Sites switch once TemputechService checks in — or restart the service on GoToAssist to apply immediately. Sites without a version are not ready for automatic updates."
                                        : "Select a version for this site and click Save. Running shows what TemputechService last reported; red text means it does not match the pin. That site will switch once TemputechService checks. You can optionally go to that site on GoToAssist and simply close TemputechService to immediately make the switch. Sites without a version are sites that are not ready to update automatically."}
                                </span>
                            }
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: 360,
                                        fontSize: 12,
                                        lineHeight: 1.4,
                                    },
                                },
                            }}
                        >
                            <MuiMaterials.IconButton
                                size="small"
                                aria-label="Versioning help"
                                sx={{ color: text, flexShrink: 0 }}
                            >
                                <HelpOutlineIcon fontSize="small" />
                            </MuiMaterials.IconButton>
                        </MuiMaterials.Tooltip>
                        {isAllSites ? (
                            <MuiMaterials.Button
                                size="small"
                                variant="contained"
                                disabled={loading || dirtySites.length === 0 || !!savingGuid}
                                onClick={handleSaveAllChanged}
                                sx={{ whiteSpace: "nowrap", flexShrink: 0, ...containedActionButtonSx() }}
                            >
                                {savingGuid === "__all__"
                                    ? "Saving…"
                                    : dirtySites.length > 0
                                      ? `Save (${dirtySites.length})`
                                      : "Save"}
                            </MuiMaterials.Button>
                        ) : null}
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            disabled={loading}
                            onClick={() => {
                                loadCatalog();
                                loadIssues();
                            }}
                            sx={{ whiteSpace: "nowrap", flexShrink: 0, ...outlinedActionButtonSx() }}
                        >
                            Refresh
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                    ) : null
                }
                className="siteFilterModal hide-scrollbar"
                loadingOverlay={phase === "edit" && loading ? <CenteredLoadingOverlay /> : null}
            >
                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={(guid) => {
                            setPickerSiteGuid(guid);
                            setSelectedSiteGuid(guid);
                        }}
                        onCancel={handleClose}
                        loading={loading}
                        filterSite={(s) => !s.isHms}
                        includeAllSites
                    />
                ) : (
                <ModalBody className="siteFilterModalContent hide-scrollbar">
                    {message ? (
                        <MuiMaterials.Typography
                            sx={{
                                fontSize: "13px",
                                color:
                                    message.type === "error"
                                        ? getThemeColor("#b00020", "#ff8a80")
                                        : message.type === "success"
                                          ? getThemeColor("#2e7d32", "#81c784")
                                          : subtle,
                                marginBottom: "8px",
                                flexShrink: 0,
                                textAlign: "left",
                            }}
                        >
                            {message.text}
                        </MuiMaterials.Typography>
                    ) : null}

                    <ModalContentTile>
                    <MuiMaterials.Box
                        ref={scrollContainerRef}
                        className="hide-scrollbar"
                        sx={{
                            width: "100%",
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                        }}
                    >
                        {loading ? null : (
                            <>
                                {scopedSites.length === 0 ? (
                                    <MuiMaterials.Typography sx={{ color: subtle, textAlign: "center", py: 2, mb: 2 }}>
                                        {isAllSites
                                            ? "No sites are set up for versioning."
                                            : "This site is not set up for versioning."}
                                    </MuiMaterials.Typography>
                                ) : (
                                    scopedSites.map((site) => {
                                        const siteGuid = site.customerSiteGuid;
                                        const draft = draftVersions[siteGuid] ?? site.version ?? "";
                                        const dirty = draft !== (site.version || "");
                                        const reported = String(site.reportedVersion || "").trim();
                                        const pinned = String(site.version || "").trim();
                                        const runningMismatch =
                                            Boolean(reported && pinned) && !versionsMatch(reported, pinned);
                                        const siteIssue =
                                            siteIssues[global.normalizeGuidForCompare(siteGuid)];
                                        const lastScanInfo =
                                            lastScanByGuid[global.normalizeGuidForCompare(siteGuid)];
                                        const windowedOptions =
                                            !versionListLimit || versionListLimit >= versionOptions.length
                                                ? versionOptions
                                                : versionOptions.slice(0, versionListLimit);
                                        const options = [...windowedOptions];
                                        if (draft && !options.some((o) => o.version === draft)) {
                                            options.unshift({
                                                version: draft,
                                                datePublished: null,
                                                comments: "",
                                            });
                                        }

                                        return (
                                            <MuiMaterials.Box
                                                key={siteGuid}
                                                onWheel={forwardWheelToScroller}
                                                onClick={
                                                    isAllSites
                                                        ? () => {
                                                            if (keepUnsavedAllSites(siteGuid)) return;
                                                            focusSiteInVersioning(siteGuid);
                                                        }
                                                        : undefined
                                                }
                                                sx={{
                                                    backgroundColor: bg,
                                                    border: `1px solid ${border}`,
                                                    ...(isAllSites && !unsavedAllSites ? { cursor: "pointer" } : {}),
                                                    p: 1.5,
                                                    mb: 1,
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 1.5,
                                                    alignItems: "center",
                                                    background: dirty
                                                        ? getThemeColor("#fff8e6", "#3d361f")
                                                        : bg,
                                                    ...(isAllSites ? { cursor: "pointer" } : {}),
                                                }}
                                            >
                                                <MuiMaterials.Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                                                    <MuiMaterials.Typography
                                                        sx={{ fontWeight: 600, color: text, fontSize: "14px" }}
                                                    >
                                                        {site.siteName || siteGuid}
                                                    </MuiMaterials.Typography>
                                                    <MuiMaterials.Typography
                                                        sx={{ color: subtle, fontSize: "12px" }}
                                                    >
                                                        Modified {formatDate(site.dateModified)}
                                                        {site.getLatestVersion
                                                            ? " · GetLatest pending"
                                                            : ""}
                                                    </MuiMaterials.Typography>
                                                    <MuiMaterials.Typography
                                                        sx={{
                                                            color: runningMismatch
                                                                ? getThemeColor("#d32f2f", "#ff6b6b")
                                                                : subtle,
                                                            fontSize: "12px",
                                                            fontWeight: runningMismatch ? 600 : 400,
                                                        }}
                                                    >
                                                        {reported
                                                            ? `Running ${reported}${
                                                                  site.reportedVersionUtc
                                                                      ? ` · reported ${formatReportedUtc(site.reportedVersionUtc)}`
                                                                      : ""
                                                              }${runningMismatch ? " · does not match pin" : ""}`
                                                            : "Running version unknown (no OTA check yet)"}
                                                    </MuiMaterials.Typography>
                                                    {lastScanInfo ? (
                                                    <SiteCardLastScanLine
                                                        lastScan={lastScanInfo.lastScan}
                                                        lastScanDate={lastScanInfo.lastScanDate}
                                                        taskSlots={lastScanInfo.taskSlots}
                                                        isHms={lastScanInfo.isHms}
                                                        isCcr={lastScanInfo.isCcr}
                                                        isLight={currentTheme === "light"}
                                                    />
                                                    ) : null}
                                                    {siteIssue ? (
                                                        <MuiMaterials.Typography
                                                            title={siteIssue}
                                                            sx={{
                                                                display: "block",
                                                                margin: 0,
                                                                width: "100%",
                                                                color: getThemeColor("#d32f2f", "#ff6b6b"),
                                                                fontSize: { xs: 10, sm: 11, md: 12 },
                                                                fontWeight: 600,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                opacity: 0.95,
                                                                letterSpacing: "0.3px",
                                                            }}
                                                        >
                                                            ⚠ {siteIssue}
                                                        </MuiMaterials.Typography>
                                                    ) : null}
                                                </MuiMaterials.Box>

                                                <MuiMaterials.FormControl
                                                    size="small"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    sx={{ flex: "0 1 380px", minWidth: 320, maxWidth: "100%" }}
                                                >
                                                    {draft ? (
                                                        <MuiMaterials.InputLabel
                                                            id={`ver-${siteGuid}`}
                                                            shrink
                                                        >
                                                            Version
                                                        </MuiMaterials.InputLabel>
                                                    ) : null}
                                                    <MuiMaterials.Select
                                                        labelId={draft ? `ver-${siteGuid}` : undefined}
                                                        label={draft ? "Version" : undefined}
                                                        notched={Boolean(draft)}
                                                        displayEmpty
                                                        value={draft}
                                                        onChange={(e) =>
                                                            handleVersionChange(siteGuid, e.target.value)
                                                        }
                                                        disabled={!!savingGuid}
                                                        SelectDisplayProps={{
                                                            onWheel: forwardWheelToScroller,
                                                        }}
                                                        renderValue={(selected) => {
                                                            if (!selected) {
                                                                return (
                                                                    <MuiMaterials.Box
                                                                        component="span"
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            width: "100%",
                                                                            minHeight: "1.4375em",
                                                                            lineHeight: 1.4375,
                                                                            color: text,
                                                                        }}
                                                                    >
                                                                        Version
                                                                    </MuiMaterials.Box>
                                                                );
                                                            }
                                                            const match = options.find(
                                                                (o) => o.version === selected,
                                                            );
                                                            const dateLabel = formatVersionDate(
                                                                match?.datePublished,
                                                            );
                                                            const comment = match?.comments || "";
                                                            const base = dateLabel
                                                                ? `${selected} (${dateLabel})`
                                                                : selected;
                                                            return comment
                                                                ? `${base} — ${comment}`
                                                                : base;
                                                        }}
                                                        sx={{
                                                            "& .MuiSelect-select": {
                                                                display: "flex",
                                                                alignItems: "center",
                                                            },
                                                        }}
                                                        MenuProps={withScrollHintMenuProps({
                                                            PaperProps: {
                                                                sx: {
                                                                    backgroundColor: bg,
                                                                    color: text,
                                                                },
                                                            },
                                                        })}
                                                    >
                                                        {options.length === 0 ? (
                                                            <MuiMaterials.MenuItem value="" disabled>
                                                                No published versions found
                                                            </MuiMaterials.MenuItem>
                                                        ) : (
                                                            options.map((opt) => {
                                                                const dateLabel = formatVersionDate(
                                                                    opt.datePublished,
                                                                );
                                                                const comment = opt.comments || "";
                                                                const label = dateLabel
                                                                    ? `${opt.version} (${dateLabel})`
                                                                    : opt.version;
                                                                return (
                                                                    <MuiMaterials.MenuItem
                                                                        key={opt.version}
                                                                        value={opt.version}
                                                                        title={comment || undefined}
                                                                    >
                                                                        <MuiMaterials.Box
                                                                            sx={{
                                                                                display: "flex",
                                                                                flexDirection: "column",
                                                                                py: 0.25,
                                                                            }}
                                                                        >
                                                                            <span>{label}</span>
                                                                            {comment ? (
                                                                                <span
                                                                                    style={{
                                                                                        fontSize: "11px",
                                                                                        opacity: 0.75,
                                                                                        overflow: "hidden",
                                                                                        textOverflow:
                                                                                            "ellipsis",
                                                                                        whiteSpace: "nowrap",
                                                                                        maxWidth: 340,
                                                                                    }}
                                                                                >
                                                                                    {comment}
                                                                                </span>
                                                                            ) : null}
                                                                        </MuiMaterials.Box>
                                                                    </MuiMaterials.MenuItem>
                                                                );
                                                            })
                                                        )}
                                                    </MuiMaterials.Select>
                                                </MuiMaterials.FormControl>

                                                <MuiMaterials.Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={!dirty || !!savingGuid || !draft}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSave(site);
                                                    }}
                                                    sx={containedActionButtonSx()}
                                                >
                                                    {savingGuid === siteGuid ? "Saving…" : "Save"}
                                                </MuiMaterials.Button>
                                            </MuiMaterials.Box>
                                        );
                                    })
                                )}

                                <MuiMaterials.Paper
                                    elevation={0}
                                    sx={{
                                        backgroundColor: bg,
                                        border: `1px solid ${border}`,
                                        borderRadius: "8px",
                                        mt: 1,
                                        mb: 1,
                                        overflow: "hidden",
                                    }}
                                >
                                    <MuiMaterials.Box
                                        sx={{
                                            px: 1.5,
                                            py: 1.5,
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            flexWrap: "wrap",
                                            userSelect: "none",
                                        }}
                                        onClick={() => setOtaVersionsOpen((prev) => !prev)}
                                    >
                                        <MuiMaterials.ExpandMoreIcon
                                            sx={{
                                                color: text,
                                                transform: otaVersionsOpen
                                                    ? "rotate(180deg)"
                                                    : "rotate(0deg)",
                                                transition: "transform 0.2s",
                                            }}
                                        />
                                        <MuiMaterials.Typography fontWeight={600} sx={{ color: text }}>
                                            OTA Versions
                                        </MuiMaterials.Typography>
                                        <MuiMaterials.Typography variant="body2" sx={{ color: subtle }}>
                                            ({publishedVersionRows.length})
                                        </MuiMaterials.Typography>
                                    </MuiMaterials.Box>
                                    {otaVersionsOpen ? (
                                        <MuiMaterials.Box
                                            sx={{
                                                px: 2,
                                                pt: 3,
                                                pb: 2,
                                                backgroundColor: getThemeColor("#e8e8e8", "#1e1e1e"),
                                            }}
                                        >
                                            <MuiMaterials.Box
                                                sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 1,
                                                    alignItems: "center",
                                                    mb: 1,
                                                }}
                                            >
                                                <MuiMaterials.Tooltip
                                                    arrow
                                                    placement="bottom"
                                                    title={
                                                        <span>
                                                            Notes from package-release (-m / -ReleaseNotes) land here and can
                                                            be edited. Saving stores them in the database and mirrors them into
                                                            each package manifest. Delete removes that version&apos;s package
                                                            files (zip, manifest, and flat folder) and its database note,
                                                            unless a site is pinned to it or still running it.
                                                        </span>
                                                    }
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                maxWidth: 360,
                                                                fontSize: 12,
                                                                lineHeight: 1.4,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <span>
                                                        <MuiMaterials.Button
                                                            size="small"
                                                            variant="outlined"
                                                            disabled={!!savingGuid || !!deletingVersion || purgingUnused || publishedVersionRows.length === 0}
                                                            onClick={() => setPurgeConfirmOpen(true)}
                                                            sx={outlinedActionButtonSx()}
                                                        >
                                                            Remove unused without notes
                                                        </MuiMaterials.Button>
                                                    </span>
                                                </MuiMaterials.Tooltip>
                                                {publishedVersionRows.length > 0 ? (
                                                    <VersionListLimitBar
                                                        total={publishedVersionRows.length}
                                                        limit={versionListLimit}
                                                        subtle={subtle}
                                                        disabled={!!savingGuid || !!deletingVersion || purgingUnused}
                                                        onMore={showVersionListMore}
                                                        onAll={showVersionListAll}
                                                        onLatest={showVersionListLatest}
                                                        sx={{ mb: 0 }}
                                                    />
                                                ) : null}
                                            </MuiMaterials.Box>
                                            {publishedVersionRows.length === 0 ? (
                                                <MuiMaterials.Typography
                                                    sx={{ color: subtle, fontSize: "13px" }}
                                                >
                                                    No published OTA versions found on the server.
                                                </MuiMaterials.Typography>
                                            ) : (
                                                <>
                                                {visiblePublishedRows.map((opt) => (
                                                    <ReleaseCommentEditor
                                                        key={`comment-${opt.version}`}
                                                        version={opt.version}
                                                        datePublished={opt.datePublished}
                                                        savedComments={opt.comments || ""}
                                                        formatVersionDate={formatVersionDate}
                                                        text={text}
                                                        border={border}
                                                        sectionBg={sectionBg}
                                                        fieldBg={fieldBg}
                                                        getThemeColor={getThemeColor}
                                                        disabled={!!savingGuid || !!deletingVersion || purgingUnused}
                                                        onSaved={handleCommentSaved}
                                                        onMessage={setMessage}
                                                        onRequestDelete={handleRequestDeleteVersion}
                                                    />
                                                ))}
                                                </>
                                            )}
                                        </MuiMaterials.Box>
                                    ) : null}
                                </MuiMaterials.Paper>
                            </>
                        )}
                    </MuiMaterials.Box>
                    </ModalContentTile>
                </ModalBody>
                )}
            </ModalChrome>

            <MuiMaterials.Modal
                open={!!deleteConfirmVersion}
                onClose={() => {
                    if (deletingVersion) return;
                    setDeleteConfirmVersion(null);
                }}
                aria-labelledby="delete-ota-version-title"
                sx={{
                    backgroundColor: getThemeColor("rgba(255, 255, 255, 0.45)", "rgba(0, 0, 0, 0.45)"),
                }}
            >
                <MuiMaterials.Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "min(440px, 92vw)",
                        bgcolor: bg,
                        border: `2px solid ${getThemeColor("#cccccc", "#f4cc8e")}`,
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 1,
                    }}
                >
                    <MuiMaterials.Typography
                        id="delete-ota-version-title"
                        sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: getThemeColor("#000000", "#f4cc8e"),
                            mb: 1,
                        }}
                    >
                        Delete OTA version?
                    </MuiMaterials.Typography>
                    <MuiMaterials.Typography sx={{ fontSize: "13px", color: subtle, mb: 2 }}>
                        This permanently removes version{" "}
                        <strong style={{ color: text }}>{deleteConfirmVersion}</strong> from the
                        server: the package zip, manifest, and flat files for every platform, plus
                        the database note. If a site is pinned to this version or still running it,
                        nothing is deleted and those sites are listed. This cannot be undone.
                    </MuiMaterials.Typography>
                    <MuiMaterials.Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            disabled={!!deletingVersion}
                            onClick={() => setDeleteConfirmVersion(null)}
                            sx={outlinedActionButtonSx()}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="contained"
                            disabled={!!deletingVersion}
                            onClick={handleConfirmDeleteVersion}
                            sx={containedActionButtonSx()}
                        >
                            {deletingVersion ? "Deleting…" : "Delete"}
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>

            <MuiMaterials.Modal
                open={purgeConfirmOpen}
                onClose={() => {
                    if (purgingUnused) return;
                    setPurgeConfirmOpen(false);
                }}
                aria-labelledby="purge-ota-versions-title"
                sx={{
                    backgroundColor: getThemeColor("rgba(255, 255, 255, 0.45)", "rgba(0, 0, 0, 0.45)"),
                }}
            >
                <MuiMaterials.Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "min(480px, 92vw)",
                        bgcolor: bg,
                        border: `2px solid ${getThemeColor("#cccccc", "#f4cc8e")}`,
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 1,
                    }}
                >
                    <MuiMaterials.Typography
                        id="purge-ota-versions-title"
                        sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: getThemeColor("#000000", "#f4cc8e"),
                            mb: 1,
                        }}
                    >
                        Remove unused versions without notes?
                    </MuiMaterials.Typography>
                    <MuiMaterials.Typography sx={{ fontSize: "13px", color: subtle, mb: 2 }}>
                        This deletes every published OTA version that has no notes and that no site
                        is pinned to or still running. Each one is removed from the server: package
                        zip, manifest, flat files, and any database note. Versions with notes are
                        kept. Versions a site is using are kept. This cannot be undone.
                    </MuiMaterials.Typography>
                    <MuiMaterials.Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            disabled={purgingUnused}
                            onClick={() => setPurgeConfirmOpen(false)}
                            sx={outlinedActionButtonSx()}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="contained"
                            disabled={purgingUnused}
                            onClick={handleConfirmPurgeUnused}
                            sx={containedActionButtonSx()}
                        >
                            {purgingUnused ? "Removing…" : "Remove them"}
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>
        </MuiMaterials.ThemeProvider>
    );
};

export default SiteVersioningModal;
